import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { hasNoRegister, isIpadViewAvailable } from '../utils/ipadView';
import {
	buildIpadSections,
	colorOf,
	fetchIpadSource,
	formatIpadPrice,
	withAlpha,
} from '../utils/ipadSource';
import { getColors, getSelectedMenu } from '../selectors/global.selector';
import './IpadView.css';

// Les niveaux de menu se lisent dans la liste des menus.
const ROUTE = { menuLevels: 'productMenu' };

/**
 * Écran par-dessus la page : la grille telle qu'elle est sur la caisse
 * (tables sources), avec le design de la prise de commande. Lecture seule,
 * rien n'est gardé : chaque ouverture relit la caisse.
 */
export const IpadViewOverlay = ({ source, parentId, idMenu, colors, hideDisabled, onClose }) => {
	const [state, setState] = useState({ status: 'loading', sections: [] });

	useEffect(() => {
		let alive = true;
		fetchIpadSource(ROUTE[source] || source, idMenu).then(
			list => {
				if (alive) {
					setState({
						status: 'done',
						sections: buildIpadSections(source, list, parentId, hideDisabled),
					});
				}
			},
			() => alive && setState({ status: 'error', sections: [] })
		);
		return () => {
			alive = false;
		};
	}, [source, parentId, idMenu, hideDisabled]);

	useEffect(() => {
		const onKey = e => e.key === 'Escape' && onClose();
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [onClose]);

	const { status, sections } = state;
	let message = null;
	if (status === 'loading') message = I18n.t('ipad_view_loading');
	else if (status === 'error') message = I18n.t('ipad_view_source_error');
	else if (!sections.length) message = I18n.t('ipad_view_source_empty');

	return createPortal(
		<div className="ipad-view-backdrop" onClick={onClose}>
			<div className="ipad-view-screen" onClick={e => e.stopPropagation()}>
				<div className="ipad-view-header">
					<span>
						{I18n.t('ipad_view')}{' '}
						<span className="ipad-view-subtitle">{I18n.t('ipad_view_readonly')}</span>
					</span>
					<IconButton onClick={onClose} aria-label={I18n.t('close')}>
						<CloseIcon />
					</IconButton>
				</div>
				<div className="ipad-view-content">
					{message ? <div className="ipad-view-message">{message}</div> : null}
					{sections.map((section, s) => (
						<div key={s}>
							{section.title != null ? (
								<div
									className="ipad-view-section"
									style={
										section.titleColorId
											? { color: colorOf({ id_color: section.titleColorId }, colors) }
											: undefined
									}>
									{section.title}
								</div>
							) : null}
							{section.layout === 'supplement' ? (
								<div className="ipad-view-grid ipad-view-grid-supp">
									{section.cells.map((cell, c) => {
										const disabled = cell.active != null && !Number(cell.active);
										const price = formatIpadPrice(cell, I18n.t('product.free_price'));
										return (
											<div
												key={c}
												className={
													'ipad-view-supp' + (disabled ? ' ipad-view-supp-disabled' : '')
												}
												style={{
													backgroundColor: withAlpha(
														disabled ? '#9e9e9e' : colorOf(cell, colors),
														0.2
													),
												}}>
												<div className={price ? 'ipad-view-supp-name-small' : ''}>
													{cell.name}
												</div>
												{price ? <div className="ipad-view-supp-price">{price}</div> : null}
											</div>
										);
									})}
								</div>
							) : (
								<div className="ipad-view-grid">
									{section.cells.map((cell, c) =>
										cell.isSpace ? (
											<div key={c} className="ipad-view-cell ipad-view-space" />
										) : (
											<div
												key={c}
												className={
													'ipad-view-cell' +
													(cell.active != null && !Number(cell.active)
														? ' ipad-view-cell-disabled'
														: '')
												}
												style={{ backgroundColor: colorOf(cell, colors) }}>
												<div className="ipad-view-name">{cell.name}</div>
												<div className="ipad-view-price">
													{formatIpadPrice(cell, I18n.t('product.free_price'))}
												</div>
											</div>
										)
									)}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>,
		document.body
	);
};

IpadViewOverlay.propTypes = {
	source: PropTypes.oneOf(['product', 'supplement', 'productMenu', 'menuLevels'])
		.isRequired,
	parentId: PropTypes.string,
	idMenu: PropTypes.string,
	colors: PropTypes.array,
	hideDisabled: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
};

/**
 * Bouton « Vue iPad » d'une grille, pour tous les établissements. Les
 * indisponibles sont retirés si la caisse les cache (hide_disabled_product à
 * 1), sinon grisés à leur place. Grisé au siège et en zone : pas de caisse.
 */
export const IpadViewButton = ({ style, noRegister, ...overlayProps }) => {
	const [open, setOpen] = useState(false);

	return (
		<div style={{ textAlign: 'right', width: '100%', ...style }}>
			{/* Le span porte l'infobulle : un bouton désactivé ne la déclenche pas. */}
			<span title={noRegister ? I18n.t('ipad_view_no_register') : undefined}>
				<Button variant="contained" disabled={!!noRegister} onClick={() => setOpen(true)}>
					{I18n.t('ipad_view')}
				</Button>
			</span>
			{open ? (
				<IpadViewOverlay {...overlayProps} onClose={() => setOpen(false)} />
			) : null}
		</div>
	);
};

const mapStateToProps = state => ({
	hideDisabled: isIpadViewAvailable(state.entity.variable),
	noRegister: hasNoRegister({
		isMaster: !!state.user.master,
		accessMode: state.access.mode,
		masterMonoChild: window.sessionStorage.getItem('master_mono_child'),
		idZone: window.sessionStorage.getItem('id_zone'),
	}),
	idMenu: getSelectedMenu(state.entity.variableServer),
	colors: getColors(state),
});

export default connect(mapStateToProps)(IpadViewButton);
